import { useState } from "react";
import {
  IconButton as MuiIconButton,
  Link as MuiLink,
  Stack as MuiStack,
  Typography as MuiTypography,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  Analytics as MuiAnalyticsIcon,
  CheckCircle as MuiCheckCircleIcon,
  DoNotDisturbOn as MuiDoNotDisturbOnIcon,
  Info as MuiInfoIcon,
  OpenInNew as MuiOpenInNewIcon,
  WatchLater as MuiWatchLaterIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Theme } from "common";
import InfoDialog from "app/shared/UI/Dialog/InfoDialog";
import {
  INFO_DIALOG_KEYS,
  METRICS_STATUS_LABELS,
} from "app/services/StudyRecruitmentService/constants";
import { useStudyRecruitment } from "app/services/StudyRecruitmentService/StudyRecruitmentProvider";

const boxStyles = {
  padding: "16px",
  backgroundColor: Theme.palette.primary.contrastText,
  borderRadius: "8px",
  boxShadow: "2px 10px 10px rgba(0, 0, 0, 0.1)",
  width: "268px",
  border: "1px solid #C4C4C4",
};

const labelStyles = {
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "18px",
};
const valueIconStyles = { height: "16px", width: "16px", marginTop: "2px" };
const valueStyles = {
  fontSize: "15px",
  lineHeight: "19px",
  whiteSpace: "pre-wrap",
};
const infoIconStyles = {
  button: { p: 0 },
  icon: { width: "18px", height: "18px" },
};

const StudyRecruitmentStatsPanel = () => {
  const { t } = useTranslation();
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [infoDialogKey, setInfoDialogKey] = useState(null);
  const { studyRecruitmentMetrics } = useStudyRecruitment();

  const {
    oncoreStatus,
    targetAccrual,
    totalAccrual,
    percentAccrued,
    studyStatus,
    studyListingStatus,
    lastOncoreSync,
    schoolOfMedicineUrl,
    stanfordHealthCareUrl,
    pageViews,
    enrolledCountFromCt,
    isHonestBrokerWorkflowEnabled,
    isStudyTeamOutreachEnabled,
    totalOutreachCount,
    totalInterestedCount,
  } = studyRecruitmentMetrics;

  const {
    NEW,
    IRB_INITIAL_APPROVAL,
    IRB_STUDY_CLOSURE,
    OPEN_TO_ACCRUAL,
    CLOSED_TO_ACCRUAL,
    SUSPENDED,
    TERMINATED,
    ON_HOLD,
    ABANDONED,
    SRC_OR_CRU_APPROVAL,
    READY_TO_ACTIVATE_SIGNOFF,
    UNRECOGNIZED,
    STATUS_NOT_LISTED,
    NOT_YET_RECRUITING,
    RECRUITING,
    NOT_RECRUITING,
    STUDY_COMPLETE,
    ACTIVE,
    INACTIVE,
  } = METRICS_STATUS_LABELS;

  const getStatusIcons = (status) => {
    switch (METRICS_STATUS_LABELS[status]) {
      case OPEN_TO_ACCRUAL:
      case READY_TO_ACTIVATE_SIGNOFF:
      case RECRUITING:
        return (
          <MuiCheckCircleIcon
            sx={{ ...valueIconStyles, color: Theme.palette.primary.green }}
          />
        );
      case NEW:
      case IRB_INITIAL_APPROVAL:
      case ON_HOLD:
      case SRC_OR_CRU_APPROVAL:
      case NOT_YET_RECRUITING:
        return (
          <MuiWatchLaterIcon sx={{ ...valueIconStyles, color: "#FEC51D" }} />
        );
      case IRB_STUDY_CLOSURE:
      case CLOSED_TO_ACCRUAL:
      case SUSPENDED:
      case TERMINATED:
      case ABANDONED:
      case UNRECOGNIZED:
      case STATUS_NOT_LISTED:
      case NOT_RECRUITING:
      case STUDY_COMPLETE:
      case INACTIVE:
        return (
          <MuiDoNotDisturbOnIcon
            sx={{ ...valueIconStyles, color: "#666666" }}
          />
        );
      default:
        return;
    }
  };

  const { STANFORD_CLINICAL_TRIALS_DIRECTORY, PEP_RECRUITMENT } =
    INFO_DIALOG_KEYS;

  const infoDialogTitle = () => {
    switch (infoDialogKey) {
      case STANFORD_CLINICAL_TRIALS_DIRECTORY:
        return t(
          "studyRecruitment.infoDialog.stanfordClinicalTrialsDirectory.title",
        );
      case PEP_RECRUITMENT:
        return t("studyRecruitment.infoDialog.pepRecruitment.title");
      default:
        return "";
    }
  };

  const infoDialogContent = () => {
    switch (infoDialogKey) {
      case STANFORD_CLINICAL_TRIALS_DIRECTORY:
        return t(
          "studyRecruitment.infoDialog.stanfordClinicalTrialsDirectory.content",
        );
      case PEP_RECRUITMENT:
        return t("studyRecruitment.infoDialog.pepRecruitment.content");
      default:
        return "";
    }
  };

  const handleInfoDialogOpen = (event, key) => {
    event.currentTarget?.blur();
    setInfoDialogKey(key);
    setOpenInfoDialog(true);
  };

  const handleInfoDialogClose = () => {
    setOpenInfoDialog(false);
  };

  return (
    <>
      <MuiStack gap={1}>
        <MuiTypography
          sx={{ fontWeight: 600, color: "black", fontSize: "20px" }}
        >
          {t("studyRecruitment.sectionTitles.stats")}
        </MuiTypography>
        <MuiStack gap={5} sx={boxStyles}>
          <MuiStack gap={1}>
            <MuiStack>
              <MuiTypography sx={labelStyles}>
                {t("studyRecruitment.labels.onCoreProtocolStatus")}
              </MuiTypography>
              <MuiStack direction="row" gap={0.5}>
                {getStatusIcons(oncoreStatus)}
                <MuiTypography sx={valueStyles}>
                  {t(
                    `studyRecruitment.status.${METRICS_STATUS_LABELS[oncoreStatus]}`,
                  )}
                </MuiTypography>
              </MuiStack>
              {METRICS_STATUS_LABELS[oncoreStatus] === OPEN_TO_ACCRUAL && (
                <MuiStack direction="row" gap={0.5}>
                  <MuiAnalyticsIcon sx={valueIconStyles} />
                  <MuiTypography sx={valueStyles}>
                    {t("studyRecruitment.stats.participantsAccrued", {
                      count: totalAccrual,
                      total: targetAccrual,
                      percentage: percentAccrued,
                    })}
                  </MuiTypography>
                </MuiStack>
              )}
            </MuiStack>

            <MuiStack>
              <MuiStack alignItems="center" direction="row" gap={0.5}>
                <MuiTypography sx={labelStyles}>
                  {t("studyRecruitment.labels.stanfordClinicalTrialsDirectory")}
                </MuiTypography>
                <MuiTooltip
                  title={t("studyRecruitment.tooltips.info")}
                  placement="bottom"
                >
                  <MuiIconButton
                    onClick={(event) =>
                      handleInfoDialogOpen(
                        event,
                        STANFORD_CLINICAL_TRIALS_DIRECTORY,
                      )
                    }
                    sx={infoIconStyles.button}
                  >
                    <MuiInfoIcon sx={infoIconStyles.icon} />
                  </MuiIconButton>
                </MuiTooltip>
              </MuiStack>
              {studyListingStatus === ACTIVE ? (
                <>
                  <MuiStack direction="row" gap={0.5}>
                    {getStatusIcons(studyStatus)}
                    <MuiTypography sx={valueStyles}>
                      {t(
                        `studyRecruitment.status.${METRICS_STATUS_LABELS[studyStatus]}`,
                      )}
                    </MuiTypography>
                  </MuiStack>
                  {schoolOfMedicineUrl && (
                    <MuiLink
                      href={schoolOfMedicineUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color={Theme.palette.primary.dark}
                      sx={{ display: "flex", gap: 0.5 }}
                    >
                      <MuiTypography sx={{ ...valueStyles, width: "180px" }}>
                        {t("studyRecruitment.stats.schoolOfMedicine")}
                      </MuiTypography>
                      <MuiOpenInNewIcon sx={valueIconStyles} />
                    </MuiLink>
                  )}
                  {stanfordHealthCareUrl && (
                    <MuiLink
                      href={stanfordHealthCareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color={Theme.palette.primary.dark}
                      sx={{ alignItems: "center", display: "flex", gap: 0.5 }}
                    >
                      <MuiTypography sx={valueStyles}>
                        {t("studyRecruitment.stats.stanfordHealthCare")}
                      </MuiTypography>
                      <MuiOpenInNewIcon sx={valueIconStyles} />
                    </MuiLink>
                  )}
                </>
              ) : (
                <MuiStack direction="row" gap={0.5}>
                  {getStatusIcons(studyListingStatus)}
                  <MuiTypography sx={valueStyles}>
                    {t(`studyRecruitment.status.${INACTIVE}`)}
                  </MuiTypography>
                </MuiStack>
              )}

              <MuiStack direction="row" gap={0.5}>
                <MuiAnalyticsIcon sx={valueIconStyles} />
                <MuiTypography sx={valueStyles}>
                  {t("studyRecruitment.stats.visitorsViewedStudyPage", {
                    count: pageViews,
                  })}
                </MuiTypography>
              </MuiStack>
              <MuiStack direction="row" gap={0.5}>
                <MuiAnalyticsIcon sx={valueIconStyles} />
                <MuiTypography sx={valueStyles}>
                  {t("studyRecruitment.stats.visitorsExpressedInterest", {
                    count: enrolledCountFromCt,
                  })}
                </MuiTypography>
              </MuiStack>
            </MuiStack>

            <MuiStack>
              <MuiStack alignItems="center" direction="row" gap={0.5}>
                <MuiTypography sx={labelStyles}>
                  {t("studyRecruitment.labels.pepRecruitment")}
                </MuiTypography>
                <MuiTooltip
                  title={t("studyRecruitment.tooltips.info")}
                  placement="bottom"
                >
                  <MuiIconButton
                    onClick={(event) =>
                      handleInfoDialogOpen(event, PEP_RECRUITMENT)
                    }
                    sx={infoIconStyles.button}
                  >
                    <MuiInfoIcon sx={infoIconStyles.icon} />
                  </MuiIconButton>
                </MuiTooltip>
              </MuiStack>
              <MuiStack direction="row" gap={0.5}>
                {isHonestBrokerWorkflowEnabled ? (
                  <MuiCheckCircleIcon
                    sx={{
                      ...valueIconStyles,
                      color: Theme.palette.primary.green,
                    }}
                  />
                ) : (
                  <MuiDoNotDisturbOnIcon
                    sx={{
                      ...valueIconStyles,
                      color: "#666666",
                    }}
                  />
                )}
                <MuiTypography sx={valueStyles}>
                  {isHonestBrokerWorkflowEnabled
                    ? t("studyRecruitment.stats.honestBrokerEnabled")
                    : t("studyRecruitment.stats.honestBrokerDisabled")}
                </MuiTypography>
              </MuiStack>
              <MuiStack direction="row" gap={0.5}>
                {isStudyTeamOutreachEnabled ? (
                  <MuiCheckCircleIcon
                    sx={{
                      ...valueIconStyles,
                      color: Theme.palette.primary.green,
                    }}
                  />
                ) : (
                  <MuiDoNotDisturbOnIcon
                    sx={{
                      ...valueIconStyles,
                      color: "#666666",
                    }}
                  />
                )}
                <MuiTypography sx={valueStyles}>
                  {isStudyTeamOutreachEnabled
                    ? t("studyRecruitment.stats.studyTeamOutreachEnabled")
                    : t("studyRecruitment.stats.studyTeamOutreachDisabled")}
                </MuiTypography>
              </MuiStack>
              <MuiStack direction="row" gap={0.5}>
                <MuiAnalyticsIcon sx={valueIconStyles} />
                <MuiTypography sx={valueStyles}>
                  {t("studyRecruitment.stats.peopleOutreached", {
                    count: totalOutreachCount,
                  })}
                </MuiTypography>
              </MuiStack>
              <MuiStack direction="row" gap={0.5}>
                <MuiAnalyticsIcon sx={valueIconStyles} />
                <MuiTypography sx={valueStyles}>
                  {t("studyRecruitment.stats.peopleExpressedInterest", {
                    count: totalInterestedCount,
                  })}
                </MuiTypography>
              </MuiStack>
            </MuiStack>
          </MuiStack>

          <MuiStack>
            <MuiTypography sx={labelStyles}>
              {t("studyRecruitment.labels.lastOnCoreSync")}
            </MuiTypography>

            <MuiTypography sx={valueStyles}>{lastOncoreSync}</MuiTypography>
          </MuiStack>
        </MuiStack>
      </MuiStack>

      <InfoDialog
        open={openInfoDialog}
        handleClose={handleInfoDialogClose}
        title={infoDialogTitle()}
      >
        <MuiTypography
          sx={{ fontSize: "14px", lineHeight: "18px", whiteSpace: "pre-wrap" }}
        >
          {infoDialogContent()}
        </MuiTypography>
      </InfoDialog>
    </>
  );
};

export default StudyRecruitmentStatsPanel;
